'use client'

// The toast rack. Bottom-right, above the drawers, slides in from the
// right and lets itself out. Green edge for things that worked, red for
// things that didn't; same cream card as everything else so it reads as
// the site talking, not the browser.
//
// It shares a corner with the thought stream on purpose: toasts only
// happen while a panel is open, and the panel covers the stream anyway.

import { useEffect, useRef, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { kawaii, ui } from '../../lib/ui/theme'

const LIFE_MS = 4500

function Toast({ t, onDrop }) {
  const [shown, setShown] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const timers = useRef([])

  useEffect(() => {
    // A timer, not requestAnimationFrame: rAF never fires in a hidden tab,
    // which left toasts mounted at opacity 0 forever if the tab was in the
    // background when one arrived.
    timers.current.push(setTimeout(() => setShown(true), 20))
    timers.current.push(setTimeout(() => setLeaving(true), LIFE_MS))
    timers.current.push(setTimeout(onDrop, LIFE_MS + 300))
    return () => timers.current.forEach(clearTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      role="status"
      onClick={onDrop}
      style={{
        background: kawaii.cream,
        border: kawaii.outlineThin,
        borderLeft: `5px solid ${t.good ? '#a8d6a0' : '#eda6a0'}`,
        borderRadius: 14,
        boxShadow: kawaii.dropSm,
        padding: '10px 14px',
        maxWidth: 340,
        color: kawaii.ink,
        fontFamily: ui.font,
        fontSize: 13.5,
        fontWeight: 700,
        lineHeight: 1.45,
        cursor: 'pointer',
        transform: shown && !leaving ? 'translateX(0)' : 'translateX(110%)',
        opacity: shown && !leaving ? 1 : 0,
        transition: 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 240ms ease',
        pointerEvents: 'auto',
      }}
    >
      {t.text}
    </div>
  )
}

export default function Toasts() {
  const toasts = useGameStore((s) => s.toasts)
  const dropToast = useGameStore((s) => s.dropToast)
  if (!toasts.length) return null
  return (
    <div
      className="fixed bottom-4 right-4 z-[80] flex flex-col items-end gap-2 pointer-events-none"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      aria-live="polite"
    >
      {toasts.map((t) => (
        <Toast key={t.id} t={t} onDrop={() => dropToast(t.id)} />
      ))}
    </div>
  )
}
